import { UcoastEl } from '@/scripts/core/UcoastEl'
import EmblaCarousel, {
	type EmblaCarouselType,
	type EmblaOptionsType,
} from 'embla-carousel'
import {
	addToggleThumbBtnsActive,
	getAttributeOrThrow,
	qsaOptional, qsOptional,
	qsRequired,
} from '@/scripts/core/global'
import AutoHeight from 'embla-carousel-auto-height'
import { type UcoastVideo } from '@/scripts/core/ucoast-video'

export class TabSlider extends UcoastEl {
	static htmlSelector = 'tab-slider'
	startIndex: number
	playMediaOnSelection: boolean

	constructor() {
		super()
		this.startIndex = 0
		if (this.hasAttribute('data-start-index')) {
			const startIndexStr = getAttributeOrThrow('data-start-index', this)
			this.startIndex = parseInt(startIndexStr)
		}
		this.playMediaOnSelection = this.hasAttribute(
			'data-play-media-on-selection'
		)
		this.playActiveSlide = this.playActiveSlide.bind(this)
		this.initializeVideo = this.initializeVideo.bind(this)
		const OPTIONS: EmblaOptionsType = {
			startIndex: this.startIndex,
		}
		const OPTIONS_THUMBS: EmblaOptionsType = {
			containScroll: 'keepSnaps',
			dragFree: true,
			startIndex: this.startIndex,
		}

		const viewportNodeMainCarousel = <HTMLElement>(
			qsRequired('.embla__viewport', this)
		)
		const viewportNodeThumbCarousel = <HTMLElement>(
			qsRequired('.embla-thumbs__viewport', this)
		)
		const emblaApiMain = EmblaCarousel(viewportNodeMainCarousel, OPTIONS, [
			AutoHeight(),
		])
		const emblaApiThumb = EmblaCarousel(
			viewportNodeThumbCarousel,
			OPTIONS_THUMBS
		)

		const removeThumbBtnsClickHandlers = this.addThumbBtnsClickHandlers(
			emblaApiMain,
			emblaApiThumb
		)
		const removeToggleThumbBtnsActive = addToggleThumbBtnsActive(
			emblaApiMain,
			emblaApiThumb
		)

		emblaApiMain
			.on('destroy', removeThumbBtnsClickHandlers)
			.on('destroy', removeToggleThumbBtnsActive)

		emblaApiThumb
			.on('destroy', removeThumbBtnsClickHandlers)
			.on('destroy', removeToggleThumbBtnsActive)

		if (this.playMediaOnSelection) {
			emblaApiThumb.on('init', () => {
				void this.initializeVideo()
			})
		}
	}
	override animateIn() {
		super.animateIn()
		void this.playActiveSlide()
	}
	async initializeVideo() {
		const activeVideoContainer = qsOptional(
			'.embla-thumbs__slide--selected',
			this
		)
		if (activeVideoContainer) {
			await window.Ucoast.mediaManager.loadAllInContainer(activeVideoContainer)
		}
		const inactiveVideoContainers = qsaOptional<UcoastVideo>(
			'.embla-thumbs__slide:not(.embla-thumbs__slide--selected)',
			this
		)
		if (inactiveVideoContainers) {
			for (const container of inactiveVideoContainers) {
				await window.Ucoast.mediaManager.preloadContainer(container)
			}
		}
	}
	async playActiveSlide() {
		const activeVideos = this.getActiveSlideVideos()
		const inactiveVideos = this.getInactiveSlideVideos()
		if (inactiveVideos) {
			for (const video of inactiveVideos) {
				await video.pause()
			}
		}
		if (activeVideos) {
			for (const video of activeVideos) {
				await video.play()
			}
		}
	}
	getActiveSlideVideos() {
		return qsaOptional<UcoastVideo>(
			'.embla-thumbs__slide--selected ucoast-video',
			this
		)
	}
	getInactiveSlideVideos() {
		return qsaOptional<UcoastVideo>(
			'.embla-thumbs__slide:not(.embla-thumbs__slide--selected) ucoast-video',
			this
		)
	}
	addThumbBtnsClickHandlers(
		emblaApiMain: EmblaCarouselType,
		emblaApiThumb: EmblaCarouselType
	): () => void {
		const slidesThumbs = emblaApiThumb.slideNodes()

		const scrollToIndex = slidesThumbs.map(
			(_, index) => (): void => emblaApiMain.scrollTo(index, true)
		)

		slidesThumbs.forEach((slideNode, index) => {
			slideNode.addEventListener('click', scrollToIndex[index], false)
			if (this.playMediaOnSelection) {
				slideNode.addEventListener('click', this.playActiveSlide, false)
			}
		})

		return (): void => {
			slidesThumbs.forEach((slideNode, index) => {
				slideNode.removeEventListener(
					'click',
					scrollToIndex[index],
					false
				)
				if (this.playMediaOnSelection) {
					slideNode.removeEventListener(
						'click',
						this.playActiveSlide,
						false
					)
				}
			})
		}
	}
}
